/**
 * Date arithmetic for the Content Calendar.
 *
 * Everything here works on plain "YYYY-MM-DD" strings that already
 * represent a day in the PROPERTY's timezone. Internally they are
 * handled as UTC-midnight Date objects and formatted with
 * `timeZone: "UTC"`, so the browser's own timezone can never shift a
 * day — an editor in Mexico City and one in Ensenada see the same grid.
 * The backend returns each item's `local_date` / `minutes` already
 * converted to the property timezone, so no instant math happens here.
 */

// Monday-first weeks keep Saturday + Sunday together at the end, which
// matches how the newsroom splits weekday vs weekend publishing.
export const WEEK_STARTS_ON = 1;

export const VIEWS = ["day", "week", "month"];

const pad = (n) => String(n).padStart(2, "0");

export function ymdToDate(ymd) {
  const [y, m, d] = String(ymd).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

export function dateToYmd(dt) {
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

export function isValidYmd(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return false;
  const dt = ymdToDate(value);
  return !Number.isNaN(dt.getTime()) && dateToYmd(dt) === value;
}

/** Today's date in the given IANA timezone. */
export function todayInTz(tz, now = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now);
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return `${get("year")}-${get("month")}-${get("day")}`;
  } catch {
    return dateToYmd(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())));
  }
}

export function addDays(ymd, n) {
  const dt = ymdToDate(ymd);
  dt.setUTCDate(dt.getUTCDate() + n);
  return dateToYmd(dt);
}

export function dayOfWeek(ymd) {
  return ymdToDate(ymd).getUTCDay();
}

export function startOfWeek(ymd, weekStartsOn = WEEK_STARTS_ON) {
  const diff = (dayOfWeek(ymd) - weekStartsOn + 7) % 7;
  return addDays(ymd, -diff);
}

export function startOfMonth(ymd) {
  return `${String(ymd).slice(0, 7)}-01`;
}

function daysInMonth(year, month1) {
  return new Date(Date.UTC(year, month1, 0)).getUTCDate();
}

export function addMonths(ymd, n) {
  const [y, m, d] = String(ymd).split("-").map(Number);
  const total = y * 12 + (m - 1) + n;
  const year = Math.floor(total / 12);
  const month1 = (total % 12) + 1;
  const day = Math.min(d, daysInMonth(year, month1));
  return `${year}-${pad(month1)}-${pad(day)}`;
}

export function eachDay(start, end) {
  const out = [];
  for (let d = start; d <= end; d = addDays(d, 1)) out.push(d);
  return out;
}

/**
 * Inclusive date range the backend should be asked for.
 * Month view spans whole weeks so the grid has no blank edge cells,
 * and also reports the real month bounds for dimming outside days.
 */
export function rangeForView(view, anchor, weekStartsOn = WEEK_STARTS_ON) {
  if (view === "day") return { start: anchor, end: anchor };
  if (view === "week") {
    const start = startOfWeek(anchor, weekStartsOn);
    return { start, end: addDays(start, 6) };
  }
  const monthStart = startOfMonth(anchor);
  const monthEnd = addDays(addMonths(monthStart, 1), -1);
  const start = startOfWeek(monthStart, weekStartsOn);
  const end = addDays(startOfWeek(monthEnd, weekStartsOn), 6);
  return { start, end, monthStart, monthEnd };
}

export function shiftAnchor(view, anchor, direction) {
  if (view === "day") return addDays(anchor, direction);
  if (view === "week") return addDays(anchor, 7 * direction);
  return addMonths(startOfMonth(anchor), direction);
}

function fmt(ymd, opts) {
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", ...opts }).format(ymdToDate(ymd));
}

export function formatRangeLabel(view, anchor, weekStartsOn = WEEK_STARTS_ON) {
  if (view === "day") {
    return fmt(anchor, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  }
  if (view === "month") {
    return fmt(anchor, { month: "long", year: "numeric" });
  }
  const { start, end } = rangeForView("week", anchor, weekStartsOn);
  const sameYear = start.slice(0, 4) === end.slice(0, 4);
  const sameMonth = sameYear && start.slice(5, 7) === end.slice(5, 7);
  if (sameMonth) {
    return `${fmt(start, { month: "short", day: "numeric" })} – ${fmt(end, { day: "numeric" })}, ${end.slice(0, 4)}`;
  }
  if (sameYear) {
    return `${fmt(start, { month: "short", day: "numeric" })} – ${fmt(end, { month: "short", day: "numeric" })}, ${end.slice(0, 4)}`;
  }
  return `${fmt(start, { month: "short", day: "numeric", year: "numeric" })} – ${fmt(end, { month: "short", day: "numeric", year: "numeric" })}`;
}

export function weekdayShort(ymd) {
  return fmt(ymd, { weekday: "short" });
}

export function dayNumber(ymd) {
  return Number(String(ymd).slice(8, 10));
}

/** 480 → "8:00 AM", 1230 → "8:30 PM". */
export function formatMinutes(minutes) {
  const m = ((Number(minutes) % 1440) + 1440) % 1440;
  const h24 = Math.floor(m / 60);
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${pad(m % 60)} ${h24 < 12 ? "AM" : "PM"}`;
}

/** 8 → "8 AM", 0 → "12 AM", 13 → "1 PM". Hour-rail labels. */
export function formatHour(hour) {
  const h = ((Number(hour) % 24) + 24) % 24;
  return `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? "AM" : "PM"}`;
}

/** Weekday header labels in grid order, e.g. ["Mon", …, "Sun"]. */
export function weekdayHeaders(weekStartsOn = WEEK_STARTS_ON) {
  // 2026-09-06 is a Sunday; walk forward from the configured start.
  return Array.from({ length: 7 }, (_, i) => weekdayShort(addDays("2026-09-06", weekStartsOn + i)));
}